import { useSignal } from "@preact/signals";
import type { ComponentChildren } from "preact";
import { useEffect, useRef } from "preact/hooks";

// The one popover: a button that toggles a panel anchored under it. Closes on
// a click outside the anchor or on Escape; clicks inside (forms, menu items)
// leave it open — children get `close` for the items that should dismiss it.

export function Popover({
  label,
  buttonClass,
  title,
  panel = "popover",
  children,
}: {
  label: ComponentChildren;
  buttonClass: string;
  /** Tooltip and accessible name for the button. */
  title?: string;
  /** Class of the panel: `popover` (header panels) or `menu` (row action menu). */
  panel?: string;
  children: (close: () => void) => ComponentChildren;
}) {
  const open = useSignal(false);
  const anchor = useRef<HTMLSpanElement>(null);
  const close = () => {
    open.value = false;
  };
  useEffect(() => {
    if (!open.value) return;
    const onClick = (e: MouseEvent) => {
      if (!anchor.current?.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("click", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open.value]);
  return (
    <span class="pop-anchor" ref={anchor}>
      <button
        type="button"
        class={buttonClass}
        title={title}
        aria-label={title}
        aria-expanded={open.value}
        onClick={() => {
          open.value = !open.value;
        }}
      >
        {label}
      </button>
      {open.value && <div class={panel}>{children(close)}</div>}
    </span>
  );
}
